import React, { useEffect, useState, useMemo } from "react";
import { Paper, Button, ButtonGroup, Box, Typography, Grid, IconButton, Modal, TextField, Stack } from "@mui/material";
import "../../Styles/strategy.css";
import Portfoliochart from "../Charts/PortfoliobarChart";
import StrategyPerformanceTable from "../Tables/StrategyPerformanceTable";
import { useParams, useNavigate } from 'react-router-dom';
import { GetStrategyVariance } from "../../Redux/Actions/transactionService";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../Loader/Index";
import StrategyAssetTable from "../Tables/StrategyAssetTable";
import {Strategyperformancegraph,GetAccounting, GetAccountingPortfolio} from '../../Redux/Actions/strategyService'
import DownloadIcon from "@mui/icons-material/Download";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

function StrategyPerformance() {

  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { strategyData,  loading } = useSelector((state) => state.transaction);
  const entityId= localStorage.getItem("entId")
  const { Accountingdata, AccountingPortfolio, AccountingPortfolioData, strategies, loading: portfolioLoading } = useSelector((state) => state.strategy);
  
  // Use AccountingPortfolioData if available, otherwise fall back to AccountingPortfolio
  const portfolioData = AccountingPortfolioData || AccountingPortfolio;
  const [strategy, setStrategy] = useState([]);
  const [totalPortfolio, setTotalPortfolio] = useState(0);
  const [isInvestorOpen, setIsInvestorOpen] = useState(true);
  const [isAssetOpen, setIsAssetOpen] = useState(false);
  const [strategyName, setStrategyName] = useState("Investment Fund 01");
  const [selectedTimeRange, setSelectedTimeRange] = useState("ITD");
  const [customFromDate, setCustomFromDate] = useState(null);
  const [customToDate, setCustomToDate] = useState(null);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [mtdFrequency, setMtdFrequency] = useState('DAILY');
  const [ytdFrequency, setYtdFrequency] = useState(null);
  const [itdFrequency, setItdFrequency] = useState('YEARLY');

  const [totalPeriodAdditions, setTotalPeriodAdditions] = useState(0);
  const [totalPerformanceFees, setTotalPerformanceFees] = useState(0);
  const [totalManagementFees, setTotalManagementFees] = useState(0);

  // Helper: format Date -> YYYY-MM-DD (local time)
  const formatDateForAPI = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Helper: compute from date based on selected range
  const getFromDate = (timeframe) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    switch (timeframe) {
      case "5D": {
        const d = new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000);
        d.setHours(0, 0, 0, 0);
        return d;
      }
      case "MTD": {
        const mtd = new Date(now);
        mtd.setDate(1);
        mtd.setHours(0, 0, 0, 0);
        return mtd;
      }
      case "YTD": {
        const ytd = new Date(now);
        ytd.setMonth(0, 1);
        ytd.setHours(0, 0, 0, 0);
        return ytd;
      }
      case "ITD":
        return null;
      case "Custom":
        return customFromDate;
      default:
        return null;
    }
  };

  // Helper: choose initial frequency per timeframe
  const getOptimalFrequency = (timeframe, fromDate) => {
    const now = new Date();

    switch (timeframe) {
      case "5D":
        return "DAILY";
      case "MTD":
        return mtdFrequency; // Use state-tracked frequency
      case "YTD": {
        if (ytdFrequency) {
          return ytdFrequency; // Use checked/adjusted frequency
        }
        if (!fromDate) return "MONTHLY";
        const diffMs = Math.abs(now - fromDate);
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        const months = Math.ceil(diffDays / 30);
        if (months <= 1) return "TWO_DAY";
        if (months > 1 && months <= 5) return "SEVEN_DAY";
        if (months > 5 && months <= 10) return "BI_MONTHLY";
        return "MONTHLY";
      }
      case "ITD":
        return itdFrequency;
      case "Custom":
        return "DAILY";
      default:
        return "DAILY";
    }
  };

  // Fetch accounting time‑series for chart when id or time range changes
  useEffect(() => {
    if (!id) return;

    const fromDate = getFromDate(selectedTimeRange);
    const from = formatDateForAPI(fromDate);
    const to =
      selectedTimeRange === "Custom" && customToDate
        ? formatDateForAPI(customToDate)
        : undefined;

    const frequency = getOptimalFrequency(selectedTimeRange, fromDate);

    const payload = {
      strategyId: id,
      skip: 0,
      take: 1000,
      ...(from && { from }),
      ...(to && { to }),
      frequency,
    };

    dispatch(GetAccountingPortfolio(payload));
    dispatch(GetStrategyVariance({ strategyId: id }));
  }, [id, dispatch, selectedTimeRange, customFromDate, customToDate, mtdFrequency, ytdFrequency, itdFrequency]);

  // MTD Two-Step Logic: Check if response > 20 points, re-fetch with TWO_DAY if needed
  useEffect(() => {
    if (selectedTimeRange === 'MTD' && portfolioData && !portfolioLoading) {
      const dataArray = Array.isArray(portfolioData) 
        ? portfolioData 
        : (portfolioData?.dataPoints || portfolioData?.datapoints || []);
      
      const aggregatedByDate = Array.isArray(dataArray) ? dataArray.reduce((acc, item) => {
        const dateKey = (item.createdAt || item.date || item.timestamp).split('T')[0];
        if (!acc[dateKey]) acc[dateKey] = true;
        return acc;
      }, {}) : {};
      
      const dataCount = Object.keys(aggregatedByDate).length;
      
      if (dataCount > 20 && mtdFrequency === 'DAILY') {
        setMtdFrequency('TWO_DAY');
      }
    }
  }, [portfolioData, selectedTimeRange, portfolioLoading, mtdFrequency]);

  // Reset MTD frequency when timeframe changes
  useEffect(() => {
    if (selectedTimeRange === 'MTD' && mtdFrequency !== 'DAILY') {
      setMtdFrequency('DAILY');
    }
  }, [selectedTimeRange]);

  // YTD Check Logic: Verify data points are within 5-20 range and adjust if needed
  useEffect(() => {
    if (selectedTimeRange === 'YTD' && portfolioData && !portfolioLoading) {
      const dataArray = Array.isArray(portfolioData) 
        ? portfolioData 
        : (portfolioData?.dataPoints || portfolioData?.datapoints || []);
      
      const aggregatedByDate = Array.isArray(portfolioData) ? portfolioData.reduce((acc, item) => {
        const dateKey = (item.createdAt || item.date || item.timestamp).split('T')[0];
        if (!acc[dateKey]) acc[dateKey] = true;
        return acc;
      }, {}) : {};
      
      const dataCount = Object.keys(aggregatedByDate).length;

      const now = new Date();
      const ytd = new Date(now);
      ytd.setMonth(0, 1);
      const ytdDiffTime = Math.abs(now - ytd);
      const ytdDiffDays = Math.ceil(ytdDiffTime / (1000 * 60 * 60 * 24));
      const ytdMonths = Math.ceil(ytdDiffDays / 30);
      
      let currentFreq = ytdFrequency;
      if (!currentFreq) {
        if (ytdMonths <= 1) currentFreq = 'TWO_DAY';
        else if (ytdMonths > 1 && ytdMonths <= 5) currentFreq = 'SEVEN_DAY';
        else if (ytdMonths > 5 && ytdMonths <= 10) currentFreq = 'BI_MONTHLY';
        else currentFreq = 'MONTHLY';
      }
      
      let needsAdjustment = false;
      let newFrequency = currentFreq;
      
      if (dataCount < 5) {
        if (currentFreq === 'MONTHLY') {
          newFrequency = 'BI_MONTHLY';
          needsAdjustment = true;
        } else if (currentFreq === 'BI_MONTHLY') {
          newFrequency = 'SEVEN_DAY';
          needsAdjustment = true;
        } else if (currentFreq === 'SEVEN_DAY') {
          newFrequency = 'TWO_DAY';
          needsAdjustment = true;
        }
      } else if (dataCount > 20) {
        if (currentFreq === 'TWO_DAY') {
          newFrequency = 'SEVEN_DAY';
          needsAdjustment = true;
        } else if (currentFreq === 'SEVEN_DAY') {
          newFrequency = 'BI_MONTHLY';
          needsAdjustment = true;
        } else if (currentFreq === 'BI_MONTHLY') {
          newFrequency = 'MONTHLY';
          needsAdjustment = true;
        }
      }
      
      if (needsAdjustment && currentFreq !== newFrequency) {
        setYtdFrequency(newFrequency);
      }
    }
  }, [portfolioData, selectedTimeRange, portfolioLoading, ytdFrequency]);

  // Reset YTD frequency when timeframe changes
  useEffect(() => {
    if (selectedTimeRange === 'YTD' && ytdFrequency !== null) {
      setYtdFrequency(null);
    } else if (selectedTimeRange !== 'YTD' && ytdFrequency !== null) {
      setYtdFrequency(null);
    }
  }, [selectedTimeRange]);

  // Reset ITD frequency when switching to ITD
  useEffect(() => {
    if (selectedTimeRange === 'ITD') {
      if (itdFrequency !== 'YEARLY') {
        setItdFrequency('YEARLY');
      }
    } else if (selectedTimeRange !== 'ITD' && itdFrequency !== 'YEARLY') {
      setItdFrequency('YEARLY');
    }
  }, [selectedTimeRange]);
    
  // ITD Check Logic: Cascade through frequencies to get 5-20 data points
  useEffect(() => {
    if (selectedTimeRange === 'ITD' && portfolioData && !portfolioLoading) {
      const dataArray = Array.isArray(portfolioData) 
        ? portfolioData 
        : (portfolioData?.dataPoints || portfolioData?.datapoints || []);
      
      const aggregatedByDate = Array.isArray(dataArray) ? dataArray.reduce((acc, item) => {
        const dateKey = (item.createdAt || item.date || item.timestamp).split('T')[0];
        if (!acc[dateKey]) acc[dateKey] = true;
        return acc;
      }, {}) : {};
      
      const dataCount = Object.keys(aggregatedByDate).length;
      
      const frequencyHierarchy = ['YEARLY', 'SEMI_ANNUALLY', 'MONTHLY', 'SEVEN_DAY', 'TWO_DAY'];
      const currentIndex = frequencyHierarchy.indexOf(itdFrequency);
      
      if (dataCount < 5 && currentIndex < frequencyHierarchy.length - 1) {
        const nextFrequency = frequencyHierarchy[currentIndex + 1];
        setItdFrequency(nextFrequency);
      }
    }
  }, [portfolioData, selectedTimeRange, portfolioLoading, itdFrequency]);

  useEffect(() => {
    if (id && strategies) {
      const findStrategy = strategies.find((i) => i.id == id);
      if (findStrategy) {
        setStrategyName(findStrategy?.name || "Investment Fund 01");
      }
    }
  }, [id, strategies]);
  const handleInvestorClick = () => {
    setIsInvestorOpen(true);
    setIsAssetOpen(false);
  };

  const handleAssetClick = () => {
    setIsInvestorOpen(false);
    setIsAssetOpen(true);
  };

  useEffect(() => {
    if (strategyData) {
      const totalPortfolioSum = strategyData.reduce(
        (sum, item) => sum + (item?.totalPortfolio || 0),
        0
      );
      setTotalPortfolio(totalPortfolioSum);
      setStrategy(strategyData);
    } else {
      setStrategy([]);
      setTotalPortfolio(0);
    }
    let newTotalPeriodAdditions = 0;
    let newTotalPerformanceFees = 0;
    let newTotalManagementFees = 0;
  
    if (Accountingdata && Accountingdata.length > 0) {
      newTotalPeriodAdditions = Accountingdata.reduce((sum, item) => sum + (item?.itd?.periodAdditions || 0), 0);
      newTotalPerformanceFees = Accountingdata.reduce((sum, item) => sum + (item?.itd?.performanceFees || 0), 0);
      newTotalManagementFees = Accountingdata.reduce((sum, item) => sum + (item?.itd?.managementFees || 0), 0);
    }
  
    setTotalPeriodAdditions(newTotalPeriodAdditions);
    setTotalPerformanceFees(newTotalPerformanceFees);
    setTotalManagementFees(newTotalManagementFees);
  }, [strategyData, Accountingdata]);

  const totalCost=strategyData[0]?.totalCost || "0.00"
  const fundReturn = totalCost > 0 ? ((totalPortfolio - parseFloat(totalCost)) / parseFloat(totalCost)) * 100 : 0;
  const formattedDate = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'short', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  const handleTimeRangeChange = (range) => {
    if (range === 'Custom') {
      setShowCustomDatePicker(true);
    } else {
      setSelectedTimeRange(range);
      // Reset custom dates when switching away from Custom
      setCustomFromDate(null);
      setCustomToDate(null);
    }
  };

  // Handle custom date selection
  const handleCustomDateApply = () => {
    if (customFromDate && customToDate) {
      setSelectedTimeRange('Custom');
      setShowCustomDatePicker(false);
    }
  };

  const handleCustomDateCancel = () => {
    setShowCustomDatePicker(false);
  };

  const handleCustomButtonClick = () => {
    setShowCustomDatePicker(true);
  };

  const handleEditClick = () => {
    navigate(`/editstrategy/${id}`);
  };

  const handleRebalanceClick = () => {
    navigate(`/rebalancing/${id}`);
  };

  const handleDownload = () => {
    // TODO: Implement download functionality
    console.log("Download clicked");
  };

  const handleMaximize = () => {
    // TODO: Implement maximize functionality
    console.log("Maximize clicked");
  };

    return (
      <>
        {loading ? (
          <Loader />
        ) : (
        
          <Box sx={{ backgroundColor: '#F5F5F5', minHeight: '100vh', p: 3 }}>
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography
                sx={{
                  color: 'var(--text-text-neutral-1, #141218)',
                  fontSize: '32px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '40px',
                }}
              >
                {strategyName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleEditClick}
                  sx={{
                    display: 'flex',
                    width: '120px',
                    height: '40px',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                    flexShrink: 0,
                    borderRadius: 'var(--radius-radius-1, 4px)',
                    border: '1px solid var(--M3-Tokens-sys-light-outline, #1344EB)',
                    borderColor: 'var(--M3-Tokens-sys-light-outline, #1344EB)',
                    color: '#1CA3FE',
                    backgroundColor: '#FFFFFF',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      borderColor: 'var(--M3-Tokens-sys-light-outline, #1344EB)',
                      backgroundColor: 'rgba(28, 163, 254, 0.04)',
                    },
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  onClick={handleRebalanceClick}
                  sx={{
                    display: 'flex',
                    width: '120px',
                    height: '40px',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                    flexShrink: 0,
                    backgroundColor: 'var(--button-button-bg-primary, #1344EB)',
                    color: '#FFFFFF',
                    textTransform: 'none',
                    borderRadius: 'var(--radius-radius-1, 4px)',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: '#1565C0',
                    },
                  }}
                >
                  Rebalance
                </Button>
              </Box>
            </Box>

            {/* Metrics Cards Row */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    backgroundColor: '#FFFFFF',
                    borderRadius: '8px',
                    height: '100%',
                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <Typography
                    sx={{
                      color: "#666666",
                     
                      fontSize: "16px",
                      fontWeight: "500",
                      mb: 1.5,
                    }}
                  >
                    Market Value
                  </Typography>
                  <Typography
                    sx={{
                      color: "#18275A",
                     
                      fontSize: "28px",
                      fontWeight: "700",
                      letterSpacing: "-0.5px",
                      lineHeight: 1.2,
                    }}
                  >
                    ${totalPortfolio?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    backgroundColor: '#FFFFFF',
                    borderRadius: '8px',
                    height: '100%',
                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <Typography
                    sx={{
                      color: "#666666",
                      
                      fontSize: "16px",
                      fontWeight: "500",
                      mb: 1.5,
                    }}
                  >
                    Total Capital Invested
                  </Typography>
                  <Typography
                    sx={{
                      color: "#18275A",
                     
                      fontSize: "28px",
                      fontWeight: "700",
                      letterSpacing: "-0.5px",
                      lineHeight: 1.2,
                    }}
                  >
                    ${parseFloat(totalCost)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    backgroundColor: '#FFFFFF',
                    borderRadius: '8px',
                    height: '100%',
                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <Typography
                    sx={{
                      color: "#666666",
                     
                      fontSize: "16px",
                      fontWeight: "500",
                      mb: 1.5,
                    }}
                  >
                    Revenue Earned
                  </Typography>
                  <Typography
                    sx={{
                      color: "#18275A",
                     
                      fontSize: "28px",
                      fontWeight: "700",
                      letterSpacing: "-0.5px",
                      lineHeight: 1.2,
                    }}
                  >
                    ${(totalPerformanceFees+totalManagementFees).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Performance Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                mb: 3,
              }}
            >
              {/* Top Row: Title and Icons */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: '#18275A',
                   
                  }}
                >
                  Performance
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    onClick={handleMaximize}
                    size="small"
                    sx={{
                      color: '#666666',
                      padding: '4px',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                  >
                    <FullscreenIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                  <IconButton
                    onClick={handleDownload}
                    size="small"
                    sx={{
                      color: '#666666',
                      padding: '4px',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                  >
                    <DownloadIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Second Row: Fund Return and Time Range Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#666666',
                     
                      mb: 0.5,
                    }}
                  >
                    Fund Return
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Typography
                      sx={{
                        fontSize: '36px',
                        fontWeight: 700,
                        color: '#149E3B',
                       
                        lineHeight: 1.2,
                      }}
                    >
                      {fundReturn.toFixed(2)}%
                    </Typography>
                    <ArrowUpwardIcon sx={{ color: '#149E3B', fontSize: '18px', alignSelf: 'flex-start', mt: 0.5 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#999999',
                     
                    }}
                  >
                    {formattedDate}
                  </Typography>
                </Box>
                
                {/* Time Range Buttons */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {['5D', 'MTD', 'YTD', 'ITD'].map((range) => (
                    <Button
                      key={range}
                      variant={selectedTimeRange === range ? "contained" : "outlined"}
                      onClick={() => handleTimeRangeChange(range)}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '4px',
                        minWidth: '50px',
                        height: '32px',
                        fontSize: '14px',
                        fontWeight: 500,
                        ...(selectedTimeRange === range ? {
                          backgroundColor: '#1976D2',
                          color: '#FFFFFF',
                          borderColor: '#1976D2',
                          '&:hover': {
                            backgroundColor: '#1565C0',
                            borderColor: '#1565C0',
                          },
                        } : {
                          borderColor: '#E0E0E0',
                          color: '#666666',
                          backgroundColor: '#FFFFFF',
                          '&:hover': {
                            borderColor: '#1976D2',
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                          },
                        }),
                      }}
                    >
                      {range}
                    </Button>
                  ))}
                  <Button
                    variant={selectedTimeRange === "Custom" ? "contained" : "outlined"}
                    onClick={handleCustomButtonClick}
                    endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '16px' }} />}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '4px',
                      height: '32px',
                      fontSize: '14px',
                      fontWeight: 500,
                      px: 1.5,
                      ...(selectedTimeRange === "Custom" ? {
                        backgroundColor: '#1976D2',
                        color: '#FFFFFF',
                        borderColor: '#1976D2',
                        '&:hover': {
                          backgroundColor: '#1565C0',
                          borderColor: '#1565C0',
                        },
                      } : {
                        borderColor: '#E0E0E0',
                        color: '#666666',
                        backgroundColor: '#FFFFFF',
                        '&:hover': {
                          borderColor: '#1976D2',
                          backgroundColor: 'rgba(25, 118, 210, 0.04)',
                        },
                      }),
                      '& .MuiButton-endIcon': {
                        marginLeft: '4px',
                      },
                    }}
                  >
                    Custom
                  </Button>
                </Box>
              </Box>

              {/* Chart */}
              <Box sx={{ mt: 2 }}>
                <Portfoliochart 
                  StrategyVariance={strategyData} 
                  selectedTimeRange={selectedTimeRange}
                  AccountingPortfolioData={portfolioData}
                />
              </Box>
            </Paper>

            {/* Performance Details Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#18275A',
                  
                  mb: 2.5,
                }}
              >
                Performance Details
              </Typography>
              
              {/* Tabs */}
              <Box sx={{ 
                display: 'flex', 
                gap: 3,
                borderBottom: '1px solid #E0E0E0',
                mb: 3,
              }}>
                <Button
                  onClick={handleInvestorClick}
                  disableRipple
                  sx={{
                    textTransform: 'none',
                    color: isInvestorOpen ? '#1976D2' : '#999999',
                    fontSize: '16px',
                    fontWeight: isInvestorOpen ? 600 : 500,
                    
                    px: 0,
                    py: 1.5,
                    minWidth: 'auto',
                    borderRadius: 0,
                    borderBottom: isInvestorOpen ? '2px solid #1976D2' : '2px solid transparent',
                    marginBottom: '-1px',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: isInvestorOpen ? '#1976D2' : '#666666',
                    },
                  }}
                >
                  Investor Performance
                </Button>
                <Button
                  onClick={handleAssetClick}
                  disableRipple
                  sx={{
                    textTransform: 'none',
                    color: isAssetOpen ? '#1976D2' : '#999999',
                    fontSize: '16px',
                    fontWeight: isAssetOpen ? 600 : 500,
                    px: 0,
                    py: 1.5,
                    minWidth: 'auto',
                    borderRadius: 0,
                    borderBottom: isAssetOpen ? '2px solid #1976D2' : '2px solid transparent',
                    marginBottom: '-1px',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: isAssetOpen ? '#1976D2' : '#666666',
                    },
                  }}
                >
                  Asset Performance
                </Button>
              </Box>

              <Box>
                {isInvestorOpen ? (
                  <StrategyPerformanceTable strategyObj={strategy} />
                ) : (
                  <StrategyAssetTable strategyData={strategy} />
                )}
              </Box>
            </Paper>
          </Box>
        )}

        {/* Custom Date Picker Modal */}
        <Modal
          open={showCustomDatePicker}
          onClose={handleCustomDateCancel}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '90%',
              maxWidth: '500px',
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
              border: '1px solid #E6E6E6',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              p: 3,
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Select Custom Date Range
            </Typography>
            
            <Stack spacing={3}>
              <TextField
                label="From Date"
                type="date"
                value={customFromDate ? customFromDate.toISOString().split('T')[0] : ''}
                onChange={(e) => {
                  const date = new Date(e.target.value);
                  date.setHours(0, 0, 0, 0);
                  setCustomFromDate(date);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
              
              <TextField
                label="To Date"
                type="date"
                value={customToDate ? customToDate.toISOString().split('T')[0] : ''}
                onChange={(e) => {
                  const date = new Date(e.target.value);
                  date.setHours(23, 59, 59, 999);
                  setCustomToDate(date);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                inputProps={{
                  min: customFromDate ? customFromDate.toISOString().split('T')[0] : undefined
                }}
              />
              
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={handleCustomDateCancel}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCustomDateApply}
                  disabled={!customFromDate || !customToDate}
                  sx={{
                    backgroundColor: 'var(--button-button-bg-primary, #1344EB)',
                  }}
                >
                  Apply
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Modal>
      </>
    );
  
}

export default StrategyPerformance;
